import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Loader, GripVertical, Plus, MapPin, Clock, Wallet } from 'lucide-react';
import Button from '../ui/Button';
import { ACTIVITY_CATEGORIES } from '../../constants/travelerProfiles';
import { getIcon } from '../../utils/iconResolver';
import { useTokens } from '../../contexts/TokenContext';
import TokenGateModal from '../ui/TokenGateModal';
import { TokenCost } from '../ui/TokenBadge';
import { TOKEN_COSTS } from '../../constants/tokens';

function AIAssistant({ itinerary, accommodations, activities, onActionExecuted, onAddActivity }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Working on it...');
  const [draggingId, setDraggingId] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const overviewSentRef = useRef(false);
  const { canAfford, deductTokens } = useTokens();
  const chatCost = TOKEN_COSTS.AI_CHAT_MESSAGE;

  useEffect(() => {
    const mustDos = itinerary.must_do_activities ? ` incorporating your must-haves: ${itinerary.must_do_activities}.` : '.';
    const initialMessage = {
      role: 'assistant',
      content: `Welcome! I've built your ${itinerary.trip_length}-day ${itinerary.destination} itinerary${mustDos} Once it loads I'll walk you through how it's structured. Ask me to adjust anything — pace, activities, or specific experiences.`,
      timestamp: new Date()
    };
    setMessages([initialMessage]);
    // eslint-disable-next-line
  }, []);

  // Send trip-structure overview once activities are loaded
  useEffect(() => {
    if (activities.length === 0 || overviewSentRef.current) return;
    overviewSentRef.current = true;

    // Group activities into city runs (consecutive days in same city)
    const dayCity = {};
    activities.forEach(a => { if (a.city_name && !dayCity[a.day_number]) dayCity[a.day_number] = a.city_name; });

    const runs = [];
    let runStart = null, runCity = null;
    const sortedDays = Object.keys(dayCity).map(Number).sort((a, b) => a - b);
    sortedDays.forEach((day, i) => {
      const city = dayCity[day];
      if (city !== runCity) {
        if (runCity !== null) runs.push({ city: runCity, from: runStart, to: sortedDays[i - 1] });
        runStart = day;
        runCity = city;
      }
      if (i === sortedDays.length - 1) runs.push({ city: runCity, from: runStart, to: day });
    });

    let overview = `Your ${itinerary.trip_length}-day ${itinerary.destination} trip is ready! Here's how I structured it:\n\n`;
    if (runs.length > 0) {
      runs.forEach(r => {
        const dayStr = r.from === r.to ? `Day ${r.from}` : `Days ${r.from}–${r.to}`;
        overview += `📍 ${dayStr} · ${r.city}\n`;
      });
    } else {
      overview += `📍 ${itinerary.trip_length} days in ${itinerary.destination}\n`;
    }

    if (itinerary.must_do_activities) {
      overview += `\n✓ Your must-haves (${itinerary.must_do_activities}) are woven in across the days.`;
    }

    overview += `\n\nHere's what you can do to make it yours:\n\n• **Swap anything** — just tell me, e.g. "replace the museum on day 2 with a food market"\n• **Add something** — "include a cooking class" and I'll fit it in\n• **Change the pace** — "make day 1 more relaxed" or "add a free evening"\n• **Drag to reorder** — grab the ⠿ handle on any activity\n• **Edit details** — tap any activity card to change time, notes or category\n\nWhat would you like to change?`;

    setMessages(prev => [...prev, { role: 'assistant', content: overview, timestamp: new Date() }]);
  }, [activities]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateInitialItinerary = async () => {
    setLoading(true);
    setLoadingMessage('Regenerating your itinerary...');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
    const body = JSON.stringify({
      itineraryId: itinerary.id,
      destination: itinerary.destination,
      tripLength: itinerary.trip_length,
      travelPace: itinerary.travel_pace,
      budget: itinerary.budget,
      travelerProfiles: itinerary.traveler_profiles,
      tripOrigin: itinerary.trip_origin || null,
      travelMode: itinerary.travel_mode || null,
      mustDoActivities: itinerary.must_do_activities || null,
    });

    try {
      let response;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          response = await fetch(`${apiUrl}/api/ai/generate-itinerary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });
          break;
        } catch (err) {
          if (attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
          } else {
            throw err;
          }
        }
      }

      const data = await response.json();

      if (data.success) {
        const aiMessage = {
          role: 'assistant',
          content: `I've regenerated your ${itinerary.trip_length}-day itinerary for ${itinerary.destination}. Your activities have been refreshed — check the planner on the right!`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);

        // Activities were inserted into DB by the backend — trigger refetch in parent
        if (onActionExecuted) {
          onActionExecuted({ type: 'regenerate' });
        }
      } else {
        throw new Error(data.error || 'Failed to generate itinerary');
      }
    } catch (error) {
      console.error('Error generating itinerary:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble regenerating right now. The server may be waking up — please try again in a moment.",
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
      setLoadingMessage('Working on it...');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() || loading) return;
    if (!canAfford(chatCost)) { setGateOpen(true); return; }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setLoadingMessage('Working on it...');

    // Show progressive loading messages
    const loadingTimer = setTimeout(() => {
      setLoadingMessage('Still working... (this may take a moment on first request)');
    }, 3000);

    try {
      // Build compact activities summary for context
      const activitySummary = (activities || []).map(a => ({
        day_number: a.day_number,
        title: a.title,
        category: a.category,
        location: a.location,
        city_name: a.city_name,
      }));

      // Call backend API for AI response (with cold-start retry)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
      const chatBody = JSON.stringify({
        itineraryId: itinerary.id,
        message: inputMessage,
        conversationHistory: messages,
        itineraryContext: {
          destination: itinerary.destination,
          tripLength: itinerary.trip_length,
          travelPace: itinerary.travel_pace,
          budget: itinerary.budget,
          travelerProfiles: itinerary.traveler_profiles,
          tripOrigin: itinerary.trip_origin || null,
          travelMode: itinerary.travel_mode || null,
          mustDoActivities: itinerary.must_do_activities || null,
          tripVibe: itinerary.trip_vibe || null,
          avoidList: itinerary.avoid_list || null,
          whoIsComing: itinerary.who_is_coming || null,
          startDate: itinerary.start_date ? itinerary.start_date.split('T')[0] : null,
        },
        currentActivities: activitySummary
      });

      let response;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          response = await fetch(`${apiUrl}/api/ai/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: chatBody,
          });
          break;
        } catch (err) {
          if (attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
          } else {
            throw err;
          }
        }
      }

      const data = await response.json();

      if (data.success) {
        deductTokens(chatCost);
        const aiMessage = {
          role: 'assistant',
          content: data.response,
          activities: data.updatedActivities,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);

        // If AI executed a structural action (add/delete/replace day), notify parent to refetch
        if (data.action && onActionExecuted) {
          onActionExecuted(data.action);
        }

        // If AI returned new hotel suggestions, trigger a re-fetch so Hotel panel updates
        if (data.accommodations?.length > 0 && onActionExecuted) {
          onActionExecuted({ type: 'regenerate' });
        }
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I couldn't process that request. The server might be waking up - please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      clearTimeout(loadingTimer);
      setLoading(false);
      setLoadingMessage('Working on it...');
    }
  };

  const handleDragStart = (e, activity, idx) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify(activity));
    setDraggingId(idx);
    // Create a visual drag image
    const dragImage = e.target.cloneNode(true);
    dragImage.style.opacity = '0.8';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const getCategoryColor = (category) => {
    const cat = ACTIVITY_CATEGORIES.find(c => c.value === category);
    return cat?.color || '#71717A';
  };

  // Render message content with markdown links and bold
  const renderMessageContent = (content) => {
    if (!content) return null;

    // Split on **bold** and [links](url), process each segment
    const tokenRegex = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = tokenRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      if (match[0].startsWith('**')) {
        parts.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
      } else {
        parts.push(
          <a
            key={key++}
            href={match[4]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-coral-500 hover:text-coral-600 underline font-medium inline-flex items-center gap-0.5"
            title="AI-suggested link - may not be available"
          >
            {match[3]}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block ml-0.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
          </a>
        );
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-5 py-4 bg-charcoal-500 flex-shrink-0">
        <div className="flex items-center gap-2.5 text-white">
          <div className="w-8 h-8 rounded-xl bg-naples-400/20 flex items-center justify-center">
            <Sparkles size={16} className="text-naples-300" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white leading-tight">AI Travel Assistant</h2>
            <p className="text-xs text-white/50 mt-0.5">Ask me to tweak your itinerary</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-platinum-50">
        {messages.map((message, idx) => (
          <div
            key={idx}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                message.role === 'user'
                  ? 'bg-charcoal-500 text-white rounded-br-sm'
                  : 'bg-white border border-platinum-100 text-charcoal-500 shadow-sm rounded-bl-sm'
              }`}
            >
              <div className="whitespace-pre-wrap">{renderMessageContent(message.content)}</div>

              {/* Activity Cards */}
              {message.activities && message.activities.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-bold mb-2">Suggested Activities:</p>
                  {message.activities.slice(0, 8).map((activity, actIdx) => (
                    <div
                      key={actIdx}
                      draggable
                      onDragStart={(e) => handleDragStart(e, activity, `${idx}-${actIdx}`)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white text-charcoal-500 p-3 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-all ${
                        draggingId === `${idx}-${actIdx}`
                          ? 'border-coral-400 bg-coral-50 opacity-50'
                          : 'border-platinum-300 hover:border-coral-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <GripVertical size={16} className="text-platinum-500" />
                          {(() => { const cat = ACTIVITY_CATEGORIES.find(c => c.value === activity.category); const CatIcon = getIcon(cat?.iconName); return <CatIcon size={18} style={{ color: cat?.color || '#71717A' }} />; })()}
                          <h4 className="font-bold text-sm">{activity.title}</h4>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {activity.city_name && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-charcoal-500 text-white font-medium">
                              {activity.city_name}
                            </span>
                          )}
                          <span
                            className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                            style={{ backgroundColor: getCategoryColor(activity.category) }}
                          >
                            Day {activity.day_number}
                          </span>
                        </div>
                      </div>

                      <p className="text-xs text-platinum-600 mb-2">{activity.description}</p>

                      <div className="flex items-center justify-between text-xs text-platinum-600">
                        <span className="flex items-center gap-1"><MapPin size={12} /> {activity.location}</span>
                        <span className="flex items-center gap-1"><Clock size={12} /> {activity.duration_minutes} min</span>
                        {activity.estimated_cost_min != null && (
                          <span className="flex items-center gap-1">
                            <Wallet size={12} /> ${activity.estimated_cost_min}
                            {activity.estimated_cost_max != null && `-$${activity.estimated_cost_max}`}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-platinum-400 flex items-center gap-1">
                          <GripVertical size={12} />
                          Or drag to a specific day
                        </span>
                        {onAddActivity && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddActivity(activity, activity.day_number || 1);
                            }}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors"
                          >
                            <Plus size={12} />
                            Add to Day {activity.day_number || 1}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-[10px] opacity-50 mt-1.5">
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-platinum-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader className="animate-spin text-coral-400" size={16} />
                <span className="text-sm text-charcoal-400">{loadingMessage}</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Preload / Regenerate Button */}
      <div className="px-3 py-2.5 border-t border-platinum-100 bg-white flex-shrink-0">
        <button
          onClick={generateInitialItinerary}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold text-charcoal-400 hover:text-coral-500 bg-platinum-50 hover:bg-coral-50 border border-platinum-200 hover:border-coral-200 transition-all disabled:opacity-50"
        >
          <Sparkles size={13} />
          Preload Suggested Itinerary
        </button>
      </div>

      {gateOpen && (
        <TokenGateModal
          actionLabel="AI chat message"
          cost={chatCost}
          onClose={() => setGateOpen(false)}
        />
      )}
      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-platinum-100 bg-white flex-shrink-0">
        {/* Quick chips */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[
            { label: 'More chill', msg: 'Suggest some more chill and relaxed activities' },
            { label: 'Cultural spots', msg: 'Suggest cultural spots and museums I should visit' },
            { label: 'Best restaurants', msg: 'Suggest the best restaurants and food spots' },
            { label: 'Hotel options', msg: 'Suggest more hotel options' },
          ].map(({ label, msg }) => (
            <button
              key={label}
              type="button"
              onClick={() => setInputMessage(msg)}
              className="text-xs px-2.5 py-1 bg-platinum-50 border border-platinum-200 rounded-full text-charcoal-400 hover:bg-coral-50 hover:border-coral-300 hover:text-coral-600 transition-colors font-medium"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 px-3.5 py-2.5 border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-300 bg-platinum-50 text-sm placeholder:text-platinum-400 text-charcoal-500"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !inputMessage.trim()}
            className="p-2.5 bg-coral-400 hover:bg-coral-500 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
        <div className="mt-2 flex justify-end">
          <TokenCost cost={chatCost} />
        </div>
      </form>
    </div>
  );
}

export default AIAssistant;
